import {
  useLinkProps,
  type AnyRouter,
  type LinkComponentProps,
  type RegisteredRouter,
} from "@tanstack/react-router";
import { Card } from "../ui/card";
import { cn } from "@/lib/utils";

type CardLinkSurfaceProps = {
  anchorProps: React.ComponentPropsWithRef<"a">;
  children: React.ReactNode;
  className?: string;
  srLabel: string;
};

function CardLinkSurface({
  anchorProps,
  children,
  className,
  srLabel,
}: CardLinkSurfaceProps) {
  return (
    <Card
      className={cn(
        "group hover:bg-accent relative grid grid-rows-[auto_1fr]",
        className,
      )}
    >
      <a
        {...anchorProps}
        className="focus-visible:ring-ring absolute inset-0 z-10 cursor-pointer touch-manipulation rounded-md focus:outline-none focus-visible:ring-2"
      >
        <span className="sr-only">{srLabel}</span>
      </a>
      {children}
    </Card>
  );
}

type InternalCardLinkProps<
  TRouter extends AnyRouter,
  TFrom extends string,
  TTo extends string,
  TMaskFrom extends string,
  TMaskTo extends string,
> = Omit<
  LinkComponentProps<"a", TRouter, TFrom, TTo, TMaskFrom, TMaskTo>,
  "children" | "className" | "href" | "to"
> & {
  href: TTo &
    LinkComponentProps<"a", TRouter, TFrom, TTo, TMaskFrom, TMaskTo>["to"];
  external?: false;
  children: React.ReactNode;
  className?: string;
  srLabel?: string;
};

type ExternalCardLinkProps = Omit<
  React.ComponentPropsWithRef<"a">,
  "children" | "className" | "href"
> & {
  href: string;
  external: true;
  children: React.ReactNode;
  className?: string;
  srLabel?: string;
};

function InternalCardLink<
  TRouter extends AnyRouter,
  TFrom extends string,
  TTo extends string,
  TMaskFrom extends string,
  TMaskTo extends string,
>({
  href,
  external: _external,
  children,
  className,
  srLabel = "View recipe",
  ...navigationProps
}: InternalCardLinkProps<TRouter, TFrom, TTo, TMaskFrom, TMaskTo>) {
  const anchorProps = useLinkProps<TRouter, TFrom, TTo, TMaskFrom, TMaskTo>({
    ...navigationProps,
    to: href,
  });

  return (
    <CardLinkSurface
      anchorProps={anchorProps}
      className={className}
      srLabel={srLabel}
    >
      {children}
    </CardLinkSurface>
  );
}

function ExternalCardLink({
  href,
  external: _external,
  children,
  className,
  srLabel = "View recipe",
  ...anchorProps
}: ExternalCardLinkProps) {
  return (
    <CardLinkSurface
      anchorProps={{ ...anchorProps, href }}
      className={className}
      srLabel={srLabel}
    >
      {children}
    </CardLinkSurface>
  );
}

export type CardLinkProps<
  TRouter extends AnyRouter = RegisteredRouter,
  TFrom extends string = string,
  TTo extends string = string,
  TMaskFrom extends string = TFrom,
  TMaskTo extends string = "",
> =
  | InternalCardLinkProps<TRouter, TFrom, TTo, TMaskFrom, TMaskTo>
  | ExternalCardLinkProps;

export function CardLink<
  TRouter extends AnyRouter = RegisteredRouter,
  const TFrom extends string = string,
  const TTo extends string = string,
  const TMaskFrom extends string = TFrom,
  const TMaskTo extends string = "",
>(props: CardLinkProps<TRouter, TFrom, TTo, TMaskFrom, TMaskTo>) {
  return props.external ? (
    <ExternalCardLink {...props} />
  ) : (
    <InternalCardLink {...props} />
  );
}
